import Minze, { MinzeElement, Reactive, EventListeners, Attrs } from 'minze'
import MainModule from '../../../modules/main'
import { Callback } from '../../../modules/callback'
import { config, MODALS } from '../../../constants'
import { Routing } from '../../../modules/routing'
import { EMAIL_REGEX, PASSWORD_REGEX } from '../../../modules/utils'
import { Locale } from '../../../modules/locale'
import { Font } from '../../../constants'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import axios from 'redaxios'

Locale.getInstance().registerKeySet('fr', 'create-contribution-search-or-add', {
  title: 'Recherchez le contributeur',
  description: `La Vitrine dispose d'une liste détaillée de contributeurs couvrant un large éventail d'organisations et disciplines. Vérifiez d'abord si le vôtre y apparaît. Si vous ne le trouvez pas, créez-le.`,
  contributorSearchTitle: 'Recherchez votre contributeur et sélectionnez-le',
  contributorSearchInputText: 'Rechercher un contributeur',
  or: 'ou',
  createContributorButtonTitle: 'Créer un nouveau contributeur',
  createContributorButtonLabel: 'Créer un contributeur',
})

Locale.getInstance().registerKeySet('en', 'create-contribution-search-or-add', {
  title: `Search for a Contributor`,
  description: `La Vitrine possesses a detailed list of Contributors covering a large scope of organizations and disciplines. Start by checking if yours is listed. If you can't find it, create it.`,
  contributorSearchTitle: 'Search and select your contributor',
  contributorSearchInputText: 'Search for a contributor',
  or: 'or',
  createContributorButtonTitle: 'Create a new contributor',
  createContributorButtonLabel: 'Create a contributor',
})

interface ISelectedContributor {
  contributorName: string
  contributorType: string
  contributorData: any
}

export interface LvcCreateContributionSearchOrAdd {
  flowGroup: string
  flowStep: string
  tooltipOpened: boolean
  selectedContributor: ISelectedContributor | null
}

export class LvcCreateContributionSearchOrAdd
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private flowCore: IFlowCore | undefined
  attrs: Attrs = [
    ['flow-step', ''],
    ['data-type', 'event'],
    ['flow-group', ''],
  ]

  reactive: Reactive = [
    ['existingEventData', '', false],
    ['newEventName', '', false],
    ['contributorSearchOrAddData', {}, false],
    ['selectedContributor', null, false],
  ]
  private onChangeCb: string = ''

  onChange(event: any) {
    // Store the selected contributor data
    if (event?.value && Array.isArray(event.value) && event.value.length > 0) {
      const contributor = event.value[0]
      this.selectedContributor = {
        contributorName: contributor.contributorName || '',
        contributorType: contributor.contributorType || '',
        contributorData: contributor,
      }
    } else if (typeof event?.value === 'string') {
      // User entered a new contributor name manually
      this.selectedContributor = {
        contributorName: event.value,
        contributorType: '',
        contributorData: null,
      }
    }

    // Update the flow data with the selected contributor and navigate
    if (this.flowCore) {
      this.flowCore.goTo('createContributionSelectType')
    }
  }

  onReady() {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)

    this.flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
    this.onChangeCb = Callback.attach(this.onChange.bind(this))
    this.rerender()
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  errors: any = {
    image: false,
    imageCredit: false,
    altTextFr: false,
    altTextEn: false,
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    // We get the data from the flowCore and we set the values of the inputs
    const entireEventData = data
    this.eventSearchOrAddData = {
      eventData: entireEventData?.createEventSearchOrAdd?.eventData,
      eventName: entireEventData?.createEventSearchOrAdd?.eventName,
    }

    this.existingEventData = entireEventData?.createEventSearchOrAdd?.eventData
    this.newEventName = entireEventData?.createEventSearchOrAdd?.eventName
  }

  public async serialize(): Promise<unknown> {
    return {
      eventData: (this.eventSearchOrAddData as any)?.eventData,
      eventName: (this.eventSearchOrAddData as any)?.eventName,
      contributorName: this.selectedContributor?.contributorName || '',
      contributorType: this.selectedContributor?.contributorType || '',
      contributorData: this.selectedContributor?.contributorData || null,
    }
  }

  public async validate(): Promise<boolean> {
    return true
  }

  handleOnCreateNewContributor = () => {
    const lang = Locale.language
    MainModule.getInstance().getRouting().goTo(Routing.ROUTES[lang]['partnerCreateContributor'])
  }

  html = () => `

      <lvc-box mt="24px" textAlign="left">
        <lvc-text font-preset="titleMobileSmall">
          ${Locale.getKey(`create-contribution-search-or-add.title`)}
        </lvc-text>
      </lvc-box>
      <lvc-box mt="24px" textAlign="left">
        <lvc-text font-preset="medium">
          ${Locale.getKey(`create-contribution-search-or-add.description`)}
        </lvc-text>
      </lvc-box>
      <lvc-box mt="24px">
        <lvc-contributors-search-field
          disable-add-button="true"
          on-change="${this.onChangeCb}"
          title="${Locale.getKey('create-contribution-search-or-add.contributorSearchTitle')}"
          default-input-text="${Locale.getKey('create-contribution-search-or-add.contributorSearchInputText')}"
        ></lvc-contributors-search-field>
      </lvc-box>
      <lvc-box mt="32px" mb="24px" display="flex" justifyContent="center">
        <lvc-text>${Locale.getKey('create-contribution-search-or-add.or')}</lvc-text>
      </lvc-box>
      <div class="button-title">
        ${Locale.getKey(`create-contribution-search-or-add.createContributorButtonTitle`)}
      </div>
      <lvc-button
         width="100%"
         id="create-new-contributor"
         display="flex"
         align-items="center"
         justify-content="center"
       >
         <lvc-box
           columnGap="8px"
           display="flex"
           align-items="center"
           justify-content="center"
           height="auto"
         >
           <lvc-box width="20px" height="20px">
             <lvc-add-icon></lvc-add-icon>
           </lvc-box>
            ${Locale.getKey(`create-contribution-search-or-add.createContributorButtonLabel`)}
         </lvc-box>
       </lvc-button>
  `
  css = () => `
    .button-title {
        color: #1E007D;
        display: flex;
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 3.5px;
    }
  `
  eventListeners: EventListeners = [
    ['#create-new-contributor', 'click', this.handleOnCreateNewContributor],
  ]
}
